import { forwardRef } from 'react';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Button as HeroButton, Spinner } from '@heroui/react';

/**
 * App button — now a thin adapter over the HeroUI v3 `Button` so every
 * call site renders the real HeroUI control (no per-site rewrite). The
 * legacy prop shape (variant / size / loading / leftIcon / rightIcon /
 * onClick) is preserved and mapped onto HeroUI's API.
 */

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'md' | 'sm';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'color'> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(props, ref) {
  const {
    variant = 'primary',
    size = 'md',
    loading,
    leftIcon,
    rightIcon,
    children,
    className,
    disabled,
    type,
    onClick,
    ...rest
  } = props;

  return (
    <HeroButton
      ref={ref as never}
      // HeroUI variants map 1:1 for primary/secondary/ghost/danger.
      variant={variant}
      size={size}
      type={type ?? 'button'}
      isDisabled={disabled || loading}
      isPending={loading}
      className={className}
      // Our callers wire onClick; HeroUI (React Aria) uses onPress.
      onPress={onClick ? (e) => onClick(e as never) : undefined}
      {...(rest as Record<string, unknown>)}
    >
      {loading ? <Spinner color="current" size="sm" /> : leftIcon}
      {children}
      {!loading && rightIcon}
    </HeroButton>
  );
});
